import { Box, Button, Grid, Typography } from "@mui/material";
import React, { useEffect, useState } from "react";
import {
  DataGridPremium,
  GridActionsCellItem,
  type GridColDef,
  type GridPaginationModel,
  type GridRowParams,
  type GridSortModel,
  Toolbar,
} from "@mui/x-data-grid-premium";
import { useFetch } from "../../../../services/hooks/useFetch";
import AddIcon from "@mui/icons-material/Add";
import { apiPrefix } from "../../../../utils/main";
import PromGroupDetails from "./PromGroupDetails";
import { useSearchParams } from "react-router-dom";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import CheckIcon from "@mui/icons-material/Check";

export default function PromGroupsPage() {
  function newPromGroup() {
    searchParams.set("mode", "create");
    setPromGroup({});
    window.history.pushState({}, "", "?" + searchParams.toString());
  }

  function CustomToolBar() {
    return (
      <Toolbar>
        <Button
          endIcon={<AddIcon />}
          size="medium"
          onClick={() => newPromGroup()}
        >
          NEW GROUP
        </Button>
      </Toolbar>
    );
  }

  const [paginationModel, setPaginationModel] = useState({
    pageSize: 10,
    page: 0,
  });

  const [searchParams, setSearchParams] = useSearchParams();
  const [filter, setFilter] = useState("");
  const [shouldFetchProms, setShouldFetchProms] = useState(true);
  const [sortModel, setSortModel] = useState<GridSortModel>([]);
  const [promGroup, setPromGroup] = useState<
    PromGroup | Partial<PromGroup> | undefined
  >(undefined);

  const {
    data: promGroupsList,
    fetch: fetchPromGroups,
    isLoading: promGroupsLoading,
  } = useFetch<PagedResponse<PromGroup>>();

  const promGroupTableFields: GridColDef<PromGroup>[] = [
    {
      field: "name",
      valueGetter: (_value, row) => {
        return row.name;
      },
      headerName: "Group Name",
      width: 400,
    },

    {
      field: "inUse",
      renderCell: (params) =>
        params.row.inUse ? <CheckIcon fontSize={"small"} /> : "",
      align: "center",
      headerAlign: "center",
      display: "flex",
      headerName: "In use",
      width: 75,
    },
    {
      field: "sourceSchemaIdentifier",
      valueGetter: (_value, row) => {
        return row.schemaIdentifier;
      },
      headerName: "Schema",
      width: 250,
    },
    {
      field: "actions",
      type: "actions",
      headerName: "",
      flex: 1,
      align: "center",
      getActions: ({ row }: GridRowParams<PromGroup>) => {
        return [
          <GridActionsCellItem
            key={row.id}
            icon={<ChevronRightIcon fontSize="small" />}
            label="View Prom Details"
            className="textPrimary"
            onClick={() => {
              searchParams.set("name", row.name);
              searchParams.set("mode", "modify");
              setSearchParams(searchParams);
              setPromGroup(row);
            }}
            color="inherit"
          />,
        ];
      },
    },
  ];

  useEffect(() => {
    if (shouldFetchProms && !promGroupsLoading) {
      const params = new URLSearchParams();
      params.set("page", paginationModel.page.toString());
      params.set("limit", paginationModel.pageSize.toString());
      if (sortModel.length > 0) {
        params.set(
          "sort",
          sortModel
            .map((s) => `${s.sort === "desc" ? "-" : "+"}${s.field}`)
            .join(","),
        );
      }
      if (filter !== "") {
        params.set("filter", filter);
      }
      void fetchPromGroups(`${apiPrefix}/proms?${params.toString()}`);
      setShouldFetchProms(false);
    }
  }, [
    shouldFetchProms,
    fetchPromGroups,
    promGroupsLoading,
    paginationModel,
    sortModel,
    filter,
  ]);

  return (
    <Grid
      container
      direction="column"
      className="company-settings-item"
      sx={{ flex: 1 }}
    >
      {promGroup &&
      (searchParams.get("mode") === "create" ||
        searchParams.get("mode") === "modify") ? (
        <PromGroupDetails promGroup={promGroup} />
      ) : (
        <div
          style={{
            height: "100%",
            minHeight: "400px",
            maxWidth: "calc(90vw - 15px)",
          }}
        >
          <Box className={"prom-groups"}>
            <Grid>
              <Typography variant="h4" textAlign="left">
                Prom Groups
              </Typography>
            </Grid>
            <br />
            <Grid>
              <Typography variant="body2">Manage Prom groups</Typography>
            </Grid>
            <br />

            <DataGridPremium
              disableColumnFilter
              disableColumnResize
              disableAggregation
              disableRowGrouping
              disablePivoting
              disableColumnPinning
              pageSizeOptions={[5, 10, 20]}
              pagination
              paginationMode="server"
              paginationModel={paginationModel}
              onPaginationModelChange={(model: GridPaginationModel) => {
                setShouldFetchProms(true);
                setPaginationModel(model);
              }}
              sortingMode="server"
              sortModel={sortModel}
              onSortModelChange={(model: GridSortModel) => {
                setShouldFetchProms(true);
                setSortModel(model);
              }}
              filterMode="server"
              onFilterModelChange={(filterModel) => {
                setShouldFetchProms(true);
                setFilter(filterModel.quickFilterValues?.join(" ") || "");
              }}
              rowCount={promGroupsList?.rowCount || 0}
              loading={promGroupsLoading}
              showToolbar={true}
              slots={{
                toolbar: CustomToolBar,
              }}
              slotProps={{
                loadingOverlay: {
                  variant: "linear-progress",
                },
              }}
              columnHeaderHeight={60}
              rowHeight={60}
              getRowId={(row) => row.id}
              density="compact"
              columns={promGroupTableFields}
              rows={promGroupsList?.results || []}
              rowSelection={false}
            />
          </Box>
        </div>
      )}
    </Grid>
  );
}
